<template>
  <div class="impact-configuration">
    <div class="section-header">
      <h4 class="gm-section-title">Skill Impacts</h4>
      <button type="button" @click="addImpact" class="gm-btn gm-btn--sm">
        Add Impact
      </button>
    </div>

    <div v-if="impacts.length === 0" class="gm-empty">
      <p class="gm-empty__hint">No impacts defined. Click "Add Impact" to add skill impacts.</p>
    </div>

    <div v-for="(impact, index) in impacts" :key="index" class="gm-panel impact-item">
      <div class="impact-header">
        <h5 class="gm-section-title">Impact {{ index + 1 }}</h5>
        <button type="button" @click="removeImpact(index)" class="gm-btn gm-btn--sm gm-btn--danger">
          Remove Impact
        </button>
      </div>

      <div class="impact-fields">
        <div class="form-row">
          <div class="gm-field">
            <label class="gm-label">Impact Kind</label>
            <select v-model="impact.kind" class="gm-select">
              <option :value="ImpactTypeEnum.None">None</option>
              <option :value="ImpactTypeEnum.KnockOut">Knock Out</option>
              <option :value="ImpactTypeEnum.Damage">Damage</option>
              <option :value="ImpactTypeEnum.Heal">Heal</option>
              <option :value="ImpactTypeEnum.Shield">Shield</option>
              <option :value="ImpactTypeEnum.Buff">Buff</option>
              <option :value="ImpactTypeEnum.Debuff">Debuff</option>
              <option :value="ImpactTypeEnum.Stun">Stun</option>
              <option :value="ImpactTypeEnum.Sleep">Sleep</option>
              <option :value="ImpactTypeEnum.Confusion">Confusion</option>
              <option :value="ImpactTypeEnum.Paralysis">Paralysis</option>
              <option :value="ImpactTypeEnum.Fear">Fear</option>
              <option :value="ImpactTypeEnum.Freeze">Freeze</option>
              <option :value="ImpactTypeEnum.Burn">Burn</option>
              <option :value="ImpactTypeEnum.Poison">Poison</option>
              <option :value="ImpactTypeEnum.Slow">Slow</option>
              <option :value="ImpactTypeEnum.Haste">Haste</option>
              <option :value="ImpactTypeEnum.Blind">Blind</option>
              <option :value="ImpactTypeEnum.Silence">Silence</option>
              <option :value="ImpactTypeEnum.Bleed">Bleed</option>
              <option :value="ImpactTypeEnum.Disarm">Disarm</option>
              <option :value="ImpactTypeEnum.Root">Root</option>
              <option :value="ImpactTypeEnum.EnergyDecrease">Energy Decrease</option>
              <option :value="ImpactTypeEnum.Reflect">Reflect</option>
              <option :value="ImpactTypeEnum.Absorb">Absorb</option>
              <option :value="ImpactTypeEnum.Dodge">Dodge</option>
              <option :value="ImpactTypeEnum.Resist">Resist</option>
              <option :value="ImpactTypeEnum.Immunity">Immunity</option>
              <option :value="ImpactTypeEnum.Regeneration">Regeneration</option>
              <option :value="ImpactTypeEnum.Lifesteal">Lifesteal</option>
            </select>
          </div>

          <div class="gm-field">
            <label class="gm-label">Violation Type</label>
            <select v-model="impact.type" class="gm-select">
              <option :value="ImpactViolationTypeEnum.None">None</option>
              <option :value="ImpactViolationTypeEnum.Physical">Physical</option>
              <option :value="ImpactViolationTypeEnum.Mental">Mental</option>
              <option :value="ImpactViolationTypeEnum.Energy">Energy</option>
              <option :value="ImpactViolationTypeEnum.Heat">Heat</option>
              <option :value="ImpactViolationTypeEnum.Cold">Cold</option>
              <option :value="ImpactViolationTypeEnum.Light">Light</option>
              <option :value="ImpactViolationTypeEnum.Darkness">Darkness</option>
            </select>
          </div>
        </div>

        <!-- Formula Configuration -->
        <FormulaBuilder
          v-model="impact.formula"
          :title="`Impact ${index + 1} Formula`"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { ImpactTypeEnum, ImpactViolationTypeEnum, CharacterStatEnum, type ImpactRequest, type FormulaRequest } from '@/api/dx-backend'
import FormulaBuilder from './FormulaBuilder.vue'

// Props
interface Props {
  modelValue: ImpactRequest[]
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: ImpactRequest[]]
}>()

// Reactive state
const impacts = ref<ImpactRequest[]>([...props.modelValue])

// Methods
const addImpact = () => {
  const isFirstImpact = impacts.value.length === 0

  const newImpact: ImpactRequest = {
    kind: isFirstImpact ? ImpactTypeEnum.Damage : ImpactTypeEnum.None,
    type: isFirstImpact ? ImpactViolationTypeEnum.Physical : ImpactViolationTypeEnum.None,
    formula: {
      base: isFirstImpact ? 10 : 0,
      requires: isFirstImpact ? [{
        stat: CharacterStatEnum.PhysicalStrength,
        value: 5
      }] : [],
      scaling: isFirstImpact ? [{
        stat: CharacterStatEnum.PhysicalStrength,
        value: 1.0
      }] : [],
      min_efficiency: isFirstImpact ? 0.5 : null,
      max_efficiency: isFirstImpact ? 2.0 : null
    }
  }
  impacts.value.push(newImpact)
  emitUpdate()
}

const removeImpact = (index: number) => {
  impacts.value.splice(index, 1)
  emitUpdate()
}

const emitUpdate = () => {
  emit('update:modelValue', [...impacts.value])
}

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  impacts.value = [...newValue]
}, { deep: true })
</script>

<style scoped>
.impact-configuration {
  margin-bottom: var(--gm-gap-lg);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap);
}

.impact-item {
  margin-bottom: var(--gm-gap);
}

.impact-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap);
}

.impact-fields {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
}

.form-row {
  display: flex;
  gap: var(--gm-gap);
}

.form-row .gm-field {
  flex: 1;
}
</style>
